import pkg from "../../package.json" with { type: "json" };
import nativeSpec from "../api/native-spec.json" with { type: "json" };

/**
 * Identifies CLI traffic to the Whop API. The backend's api_client classifier
 * keys on the `Whop-CLI/` prefix — every request the CLI makes must send it,
 * so all Whop API calls go through `whopFetch` rather than global `fetch`.
 */
export const CLI_USER_AGENT = `Whop-CLI/${pkg.version}`;
export const API_VERSION_DATE: string = nativeSpec.info["x-api-version-date"];

/** Global `fetch` with the CLI user agent stamped on (overriding) every request. */
export const whopFetch: typeof fetch = (input, init) => {
	const headers = new Headers(
		init?.headers ?? (input instanceof Request ? input.headers : undefined),
	);
	headers.set("User-Agent", CLI_USER_AGENT);
	return fetch(input, { ...init, headers });
};

export function whopApiFetch(
	input: string | URL | Request,
	accessToken: string,
	init?: RequestInit,
): Promise<Response> {
	const headers = new Headers(
		init?.headers ?? (input instanceof Request ? input.headers : undefined),
	);
	headers.set(
		"Authorization",
		`Bearer ${accessToken.replace(/^Bearer\s+/i, "").trim()}`,
	);
	headers.set("Api-Version-Date", API_VERSION_DATE);
	if (process.env.WHOP_APP_NAME === "whopbot") {
		headers.set("x-whop-app-name", "whopbot");
		for (const [variable, header] of [
			["WHOP_SESSION_ID", "x-whop-session-id"],
			["WHOP_TASK_ID", "x-whop-task-id"],
			["WHOP_RUN_ID", "x-whop-run-id"],
		]) {
			const value = process.env[variable];
			if (
				value?.length === 36 &&
				/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
					value,
				)
			) {
				headers.set(header, value.toLowerCase());
			}
		}
	}
	return whopFetch(input, { ...init, headers });
}

export class WhopApiError extends Error {
	constructor(
		public readonly status: number,
		message: string,
	) {
		super(message);
		this.name = "WhopApiError";
	}
}

export async function responseError(response: Response): Promise<WhopApiError> {
	const text = await response.text();
	let payload: unknown;
	try {
		payload = JSON.parse(text);
	} catch {
		payload = null;
	}
	return new WhopApiError(
		response.status,
		apiErrorMessage(payload) ||
			response.statusText ||
			`HTTP ${response.status}`,
	);
}

function apiErrorMessage(payload: unknown): string | undefined {
	if (!payload || typeof payload !== "object") return undefined;
	const response = payload as Record<string, unknown>;
	if (typeof response.message === "string") return response.message;
	if (typeof response.detail === "string") return response.detail;
	if (typeof response.error === "string") return response.error;
	if (
		response.error &&
		typeof response.error === "object" &&
		typeof (response.error as Record<string, unknown>).message === "string"
	) {
		return (response.error as Record<string, string>).message;
	}
	return undefined;
}
